import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, signal, WritableSignal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { RouterLink } from "@angular/router";
import { CampaignType, DspSupplyType, StrategyDspCreativeType, StrategyDspFunnel } from "@front/m19-api-client";
import { Marketplaces } from "@front/m19-models";
import {
  AccountSelectionService,
  dealForbidsAudienceTargeting,
  DspService,
  DspStrategyService,
  findDealWithoutAudienceTargeting,
  findProgrammaticGuaranteedDeal,
} from "@front/m19-services";
import { IButtonDirective, IToggleComponent, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DSP_BLACKLIST_URL } from "@m19-board/dsp/dsp.routes";
import { StrategyPageService } from "@m19-board/strategies/strategy-page/strategy-page.service";
import { finalize, switchMap } from "rxjs";
import { DspDealSelectorComponent } from "@m19-board/dsp/dsp-media/dsp-deal-selector.component";

type SupplyFlag =
  | "amzSupply"
  | "amzTwitchSupply"
  | "amzAlexaSupply"
  | "amzImdbSupply"
  | "apdSupply"
  | "tpeSupply"
  | "amzTwitchStvSupply"
  | "tpeStvSupply";

const supplyFlagTranslations: Record<SupplyFlag, string> = {
  amzSupply: "common.amz_supply",
  amzTwitchSupply: "common.amz_twitch_supply",
  amzAlexaSupply: "common.amz_alexa_supply",
  amzImdbSupply: "common.amz_imdb_supply",
  apdSupply: "common.apd_supply",
  tpeSupply: "common.tpe_supply",
  amzTwitchStvSupply: "common.amz_supply",
  tpeStvSupply: "common.tpe_supply",
};

const supplyFlagTypes: Record<SupplyFlag, DspSupplyType> = {
  amzSupply: DspSupplyType.AMZ,
  amzTwitchSupply: DspSupplyType.AMZ_TWITCH,
  amzAlexaSupply: DspSupplyType.AMZ_ALEXA,
  amzImdbSupply: DspSupplyType.AMZ_IMDB,
  apdSupply: DspSupplyType.APD,
  tpeSupply: DspSupplyType.TPE,
  amzTwitchStvSupply: DspSupplyType.AMZ_TWITCH_STV,
  tpeStvSupply: DspSupplyType.TPE_STV,
};

@Component({
  selector: "app-dsp-supply",
  templateUrl: "./dsp-supply.component.html",
  imports: [IToggleComponent, TranslocoDirective, CommonModule, IButtonDirective, RouterLink, DspDealSelectorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspSupplyComponent {
  readonly exclusionPageUrl = DSP_BLACKLIST_URL;

  private readonly strategyPageService = inject<StrategyPageService<CampaignType.DSP>>(StrategyPageService);
  readonly accountSelectionService = inject(AccountSelectionService);
  readonly strategyService = inject(DspStrategyService);
  readonly toasterService = inject(ToasterService);
  readonly translocoService = inject(TranslocoService);
  readonly dspService = inject(DspService);

  readonly SupplyType = DspSupplyType;
  readonly strategy = this.strategyPageService.strategy;
  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });
  readonly enabledSupplyTypes = computed(() => new Set(this.strategy()?.dspSupply?.map((s) => s.supplyType) ?? []));

  readonly isAwareness = computed(() => this.strategy()?.dspFunnel === StrategyDspFunnel.AWARENESS);
  readonly isStvMedia = computed(() => this.strategy()?.creativeType === StrategyDspCreativeType.STREAMING_TV);
  readonly isVideoDealMedia = computed(() => this.strategy()?.creativeType === StrategyDspCreativeType.VIDEO_DEAL);

  readonly videoDeals = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((accountMarketplace) => this.dspService.getDspAdvertiserDeals(accountMarketplace.dspAdvertiserId!)),
    ),
    { initialValue: [] },
  );
  readonly strategyDealIds = computed(
    () =>
      this.strategy()
        ?.dspSupply?.filter((s) => s.supplyType === DspSupplyType.VIDEO_DEAL && s.dealId)
        .map((s) => s.dealId!) ?? [],
  );
  readonly updatingDeals = signal(false);
  // The deal of a programmatic guaranteed buy is contractual: it cannot be swapped once the strategy exists.
  readonly guaranteedDeal = computed(() => findProgrammaticGuaranteedDeal(this.videoDeals(), this.strategyDealIds()));

  // Swapping to a deal that forbids audiences would leave the strategy's audiences in place, behind a hidden
  // Audiences tab: such deals are only offered when the current deal already forbids them.
  readonly offeredDeals = computed(() =>
    findDealWithoutAudienceTargeting(this.videoDeals(), this.strategyDealIds())
      ? this.videoDeals()
      : this.videoDeals().filter((deal) => !dealForbidsAudienceTargeting(deal)),
  );

  isSupplyEnabled(supplyType: DspSupplyType): boolean {
    return this.enabledSupplyTypes().has(supplyType);
  }

  onDealChange(newDealId: string | undefined) {
    if (this.isReadOnly() || this.guaranteedDeal()) return;
    const current = this.strategyDealIds();
    const newDealIds = newDealId ? [newDealId] : [];
    const suppliesToAdd = newDealIds
      .filter((dealId) => !current.includes(dealId))
      .map((dealId) => ({ supplyType: DspSupplyType.VIDEO_DEAL, dealId }));
    const suppliesToRemove = current
      .filter((dealId) => !newDealIds.includes(dealId))
      .map((dealId) => ({ supplyType: DspSupplyType.VIDEO_DEAL, dealId }));
    if (suppliesToAdd.length === 0 && suppliesToRemove.length === 0) return;

    this.updatingDeals.set(true);
    this.strategyService
      .updateStrategyDspSupplies(this.strategy()!.strategyId, suppliesToAdd, suppliesToRemove)
      .pipe(
        finalize(() => {
          this.updatingDeals.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(this.translocoService.translate("dsp-supply.deals_updated_successfully"));
        },
        error: (error) => {
          this.toasterService.error(error);
        },
      });
  }

  readonly amazonDomain = computed(() => {
    const marketplace = this.accountSelectionService.selectedMarketplace();
    return marketplace ? Marketplaces[marketplace].amazonDomain : "amazon.com";
  });

  readonly updatingSupplyFlags: Readonly<Record<SupplyFlag, WritableSignal<boolean>>> = {
    amzSupply: signal(false),
    amzTwitchSupply: signal(false),
    amzAlexaSupply: signal(false),
    amzImdbSupply: signal(false),
    apdSupply: signal(false),
    tpeSupply: signal(false),
    amzTwitchStvSupply: signal(false),
    tpeStvSupply: signal(false),
  };

  toggleSupply(supplyFlag: SupplyFlag) {
    if (this.isReadOnly()) {
      return;
    }
    this.updatingSupplyFlags[supplyFlag].set(true);
    const strategy = this.strategy()!;
    const supplyType = supplyFlagTypes[supplyFlag];
    const enabled = this.isSupplyEnabled(supplyType);
    this.strategyService
      .updateStrategyDspSupplies(strategy.strategyId, enabled ? [] : [{ supplyType }], enabled ? [{ supplyType }] : [])
      .subscribe({
        next: () => {
          const title = this.translocoService.translate(supplyFlagTranslations[supplyFlag]);
          if (enabled) {
            this.toasterService.success(this.translocoService.translate(`dsp-supply.supply_source_deactivated`), title);
          } else {
            this.toasterService.success(this.translocoService.translate(`dsp-supply.supply_source_activated`), title);
          }
          this.updatingSupplyFlags[supplyFlag].set(false);
        },
        error: (error) => {
          this.toasterService.error(error);
          this.updatingSupplyFlags[supplyFlag].set(false);
        },
      });
  }
}
